import classNames from 'classnames';
import Flex from 'components/common/Flex';
import Logo from 'components/common/Logo';
import SettingsToggle from 'components/settings-panel/SettingsToggle';
import { navbarBreakPoint } from 'config';
import { capitalize } from 'helpers/utils';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect } from 'react';
import { Col, Nav, Navbar, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import routes from 'routes/siteMaps';
import { selectAllTenants } from 'store/tenants';

import bgNavbar from 'assets/img/generic/bg-navbar.png';
import NavbarVerticalMenu from './NavbarVerticalMenu';
import ToggleButton from './ToggleButton';

import { useAppContext } from 'Main';
import { useFeatures } from 'hooks/useFeatures';
import { HELP_DESK_LINKS } from 'shared/constants/helpDeskLinks';
import { getDomainAndSubdomain } from 'shared/utils/getDomainAndSubdomain';
import { selectAllLocations } from 'store/locations';
import { selectAllPools } from 'store/pool';
import { usePermissions } from './usePermissions';

const NavbarVertical = () => {
  const locations = useSelector(selectAllLocations);
  const tenants = useSelector(selectAllTenants);
  const pools = useSelector(selectAllPools);
  const { subdomain } = getDomainAndSubdomain();
  // Plan 06 §Key contracts: 'pools' (pool configuration nav) is mining-guarded (default visible);
  // the colo nav group's non-Flags children are colo-guarded (opt-in). See the
  // featureFilteredRoutes step below.
  const { miningEnabled, coloEnabled } = useFeatures();

  const allRoutes = routes.map(route => {
    // Check if dynamic routes inside
    if (route.functionRoute) {
      // Extract required elements for this route
      const { requiredElements: dynamicRoutes } = route;

      //Map of arrays corresponding to each required element
      const arrayMap = {
        pools,
        locations,
        tenants
      };

      // Gather the arrays that correspond to the required elements
      const arraysToPass = dynamicRoutes.map(routes => arrayMap[routes] || []);

      // Call the route function with the gathered arrays
      const updatedRoute = route.route(...arraysToPass);

      return updatedRoute;
    }

    return route;
  });

  const helpDeskLink = HELP_DESK_LINKS[subdomain];

  // Plan 06 §Key contracts guard step: prune 'pools' (pool configuration nav) out of home and
  // drop the whole 'Self Mining' group (hashrate/BTC dashboards) when mining is explicitly off;
  // prune the colo-only children (sources/outlets/collectors) out of the colo group's nav entry
  // when colo isn't enabled — 'coloFlags' always stays so an admin can still reach the toggle.
  const featureFilteredRoutes = allRoutes
    .filter(route => miningEnabled || route.label !== 'Self Mining')
    .map(route => {
      if (route.label === 'home' && !miningEnabled) {
        return {
          ...route,
          children: route.children?.filter(child => child.id !== 'pools')
        };
      }
      if (route.label === 'AI' && !coloEnabled) {
        return {
          ...route,
          children: route.children?.filter(child => child.id === 'coloFlags')
        };
      }
      return route;
    });

  const finalRoutes = featureFilteredRoutes.map(route => {
    const newChildren = route.children?.map(child => {
      if (child.id === 'helpDesk') {
        if (helpDeskLink) {
          return {
            ...child,
            external: true,
            externalUrl: helpDeskLink
          };
        } else {
          return {
            ...child,
            badge: {
              text: 'Upgrade',
              type: 'secondary'
            }
          };
        }
      } else if (
        child.id === 'marketingMailCampaigns' ||
        child.id === 'marketingLandingPage' ||
        child.id === 'marketingGoogleAnalytics' ||
        child.id === 'marketingSimilarweb' ||
        child.id === 'newsletter'
      ) {
        return {
          ...child,
          badge: {
            text: 'Upgrade',
            type: 'secondary'
          }
        };
      }
      return child;
    });

    return {
      ...route,
      children: newChildren || route.children
    };
  });

  const { filterRoutesByPermissions } = usePermissions();
  const filteredRoutes = filterRoutesByPermissions(finalRoutes);

  const {
    config: { navbarStyle, isNavbarVerticalCollapsed, showBurgerMenu }
  } = useAppContext();

  const HTMLClassList = document.getElementsByTagName('html')[0].classList;

  useEffect(() => {
    if (isNavbarVerticalCollapsed) {
      HTMLClassList.add('navbar-vertical-collapsed');
    } else {
      HTMLClassList.remove('navbar-vertical-collapsed');
    }
    return () => {
      HTMLClassList.remove('navbar-vertical-collapsed-hover');
    };
  }, [isNavbarVerticalCollapsed, HTMLClassList]);

  //Control mouseEnter event
  let time = null;
  const handleMouseEnter = () => {
    if (isNavbarVerticalCollapsed) {
      time = setTimeout(() => {
        HTMLClassList.add('navbar-vertical-collapsed-hover');
      }, 100);
    }
  };
  const handleMouseLeave = () => {
    clearTimeout(time);
    HTMLClassList.remove('navbar-vertical-collapsed-hover');
  };

  const NavbarLabel = ({ label }) => (
    <Nav.Item as="li">
      <Row className="mt-3 mb-2 navbar-vertical-label-wrapper">
        <Col xs="auto" className="navbar-vertical-label navbar-vertical-label">
          {label}
        </Col>
      </Row>
    </Nav.Item>
  );

  return (
    <Navbar
      expand={navbarBreakPoint}
      className={classNames('navbar-vertical', {
        [`navbar-${navbarStyle}`]: navbarStyle !== 'transparent'
      })}
      variant="light"
    >
      <Flex alignItems="center">
        <ToggleButton />
        <Logo at="navbar-vertical" textclass="text-primary" width={40} />
      </Flex>
      <Navbar.Collapse
        in={showBurgerMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          backgroundImage:
            navbarStyle === 'vibrant'
              ? `linear-gradient(-45deg, rgba(0, 160, 255, 0.86), #0048a2),url(${bgNavbar})`
              : 'none'
        }}
      >
        {filteredRoutes ? (
          <div className="navbar-vertical-content scrollbar">
            <Nav className="flex-column" as="ul">
              {filteredRoutes.map(route => {
                return (
                  <Fragment key={route.label}>
                    {!route.labelDisable && (
                      <NavbarLabel label={capitalize(route.label)} />
                    )}
                    <NavbarVerticalMenu routes={route.children} />
                  </Fragment>
                );
              })}
            </Nav>
            <div className="mt-5">
              <SettingsToggle />
            </div>
            <p>&copy; 2024 DuckEnergy</p>
          </div>
        ) : (
          <h4 className="my-5">You have no permissions to view menu</h4>
        )}
      </Navbar.Collapse>
    </Navbar>
  );
};

NavbarVertical.propTypes = {
  label: PropTypes.string
};

export default NavbarVertical;
